import { AvatarDropdown, Footer } from "@/components";
import "@/global.less";
import type { Settings as LayoutSettings } from "@ant-design/pro-components";
import { history, useModel, type RunTimeLayoutConfig } from "@umijs/max";
import { ConfigProvider } from "antd";
import { ReactElement } from "react";
import defaultSettings from "../config/defaultSettings";
import SiteSelector from "./components/site-selector";
import { requestConfig } from "./services/request";
import { userInfo, type CurrentUser } from "./services/user";
import { getSafeRedirectPath } from "./utils/auth";

// const isDev = process.env.NODE_ENV === "development";
const loginPath = "/user/login";

/**
 * 加载当前用户及权限，登录后仅允许跳转站内页面。
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: CurrentUser;
  seatUserList?: {
    label: string;
    value: string;
    key: string;
    extra: ReactElement;
  }[];
  loading?: boolean;
  fetchUserInfo?: () => Promise<CurrentUser | undefined>;
}> {
  const access_token = localStorage.getItem("ACCESS_TOKEN");
  // 读取当前用户，失效的凭证交由登录页重新认证。
  const fetchUserInfo = async () => {
    try {
      const msg = await userInfo();
      return msg.data.user;
    } catch (_error) {}
    return undefined;
  };

  // 如果不是登录页面，执行获取用户信息
  const loginQuery = new URL(window.location.href).searchParams;
  const isDingTalkCallback = window.location.pathname === loginPath && (loginQuery.has("code") || loginQuery.has("authCode") || loginQuery.has("error"));
  if (access_token && !isDingTalkCallback) {
    const currentUser = await fetchUserInfo();
    const { location } = history;
    const urlParams = new URL(window.location.href).searchParams;

    if (currentUser && location.pathname == loginPath) {
      history.push(getSafeRedirectPath(urlParams.get("redirect")));
    }

    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const { siteList, currentSite } = useModel("site");
  return {
    avatarProps: {
      src: "",
      title: initialState?.currentUser?.name,
      render: (_, avatarChildren) => {
        return <AvatarDropdown children={avatarChildren} />;
      },
    },
    actionsRender: () => [<SiteSelector />],
    menu: {
      locale: false,
    },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name,
    // },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    // 使用 false 而非 null，匹配 WithFalse 类型签名，避免类型报错
    menuHeaderRender: null,
    // 自定义菜单项点击行为：可在这里注入埋点、重置状态、或阻止默认跳转
    menuItemRender: (item, dom) => {
      // 仅对指定菜单添加点击事件（示例：邮件菜单）
      return (
        <a
          onClick={(e) => {
            e.preventDefault();
            // 在这里添加你的点击逻辑，例如：埋点/清理筛选条件等
            // console.log("menu click:", item);
            history.push(item.path!);
          }}
        >
          {dom}
        </a>
      );
    },
    bgLayoutImgList: [
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr",
        left: 85,
        bottom: 100,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr",
        bottom: -68,
        right: -45,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr",
        bottom: 0,
        left: 0,
        width: "331px",
      },
    ],
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: "#1976d2",
            },
          }}
        >
          <div key={currentSite}>
            {children}
            {/* {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )} */}
          </div>
        </ConfigProvider>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = requestConfig;
